/**
 * kf-page — wiederverwendbares Vario-App-Fenster-Scaffold (go eCommerce Design).
 *
 * Baut den kompakten App-Header (go-eCommerce-Logo + Marktplatz-Bildmarke,
 * Titel/Eyebrow, Status, Aktions-Buttons), die schwebende Card mit Toolbar
 * (Suche + segmentierter Filter + rechte Tools) und einen ag-Grid-Mount-Punkt
 * im `ag-theme-vario`. Dazu einen Slide-in-Detail-Drawer und einen Toast.
 *
 * Das Scaffold ist daten-/grid-agnostisch: der Aufrufer erzeugt das Grid im
 * zurückgegebenen `gridEl` und verdrahtet Suche/Filter/Aktionen über die
 * Callbacks. Styles: packages/frontend-design/styles/kf-page.css (`.kf-page`).
 *
 *   const page = renderKfPage(host, {
 *     eyebrow: 'Otto · Protokoll', title: 'Log-Übersicht',
 *     status: 'Otto verbunden',
 *     actions: [{ id:'export', label:'Exportieren', icon: KF_ICON.export, variant:'ghost' },
 *               { id:'refresh', label:'Aktualisieren', icon: KF_ICON.refresh, variant:'primary' }],
 *     search: { placeholder: 'Suchen…' },
 *     segments: [{ id:'all', label:'Alle' }, { id:'err', label:'Fehler', color:'var(--danger)' }],
 *     showColConfig: true,
 *   });
 *   createGrid(page.gridEl, options);
 *   page.onSearch((t) => …); page.onSegment((id) => …); page.onAction('refresh', () => …);
 */
import { KF_LOGO_GOECOMMERCE, KF_LOGO_OTTO } from './kf-logos.js';
export { KF_LOGO_GOECOMMERCE, KF_LOGO_OTTO } from './kf-logos.js';
/** Stroke-Icons aus dem Design-Handoff (currentColor, 24×24). */
export const KF_ICON = {
    export: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5M12 15V3"/></svg>',
    refresh: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.2-8.5"/><path d="M21 3v6h-6"/></svg>',
    search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4-4"/></svg>',
    columns: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M7 12h10M10 18h4"/></svg>',
    detail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4-4"/></svg>',
    reprocess: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.2-8.5"/><path d="M21 3v6h-6"/></svg>',
    copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
    external: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>',
    help: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3 2.5V14"/><path d="M12 17h.01"/></svg>',
    truck: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 18V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l3 3v5a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="1.8"/><circle cx="17.5" cy="18.5" r="1.8"/></svg>',
    ban: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="m5.6 5.6 12.8 12.8"/></svg>',
    return: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 5 5v0a5 5 0 0 1-5 5H9"/></svg>',
    file: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>',
    cloudUp: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 13v8M8 17l4-4 4 4"/><path d="M20 16.5A4.5 4.5 0 0 0 17 8h-1.3A7 7 0 1 0 4 14.9"/></svg>',
    save: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>',
    undo: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>',
    pause: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>',
    play: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M6 4l14 8-14 8z"/></svg>',
    link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>',
};
function esc(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
/** JSON-Wert als syntax-gefärbtes HTML (keys grün, strings orange, zahlen blau) für `.payload`. */
export function kfJsonPayload(value) {
    const json = JSON.stringify(value ?? {}, null, 2);
    // Tokenize über JSON.stringify-Ausgabe: keys "x": , string-werte, zahlen.
    return esc(json)
        .replace(/&quot;([^&]*?)&quot;(\s*:)/g, '<span class="k">&quot;$1&quot;</span>$2')
        .replace(/(:\s*)&quot;([^&]*?)&quot;/g, '$1<span class="s">&quot;$2&quot;</span>')
        .replace(/(:\s*)(-?\d+(?:\.\d+)?)/g, '$1<span class="n">$2</span>');
}
export function renderKfPage(host, opts) {
    const brandHref = opts.brandHref ?? 'https://www.go-ecommerce.de';
    const actionsHtml = (opts.actions ?? [])
        .map((a) => `<button type="button" class="btn ${a.variant === 'primary' ? 'btn-primary' : 'btn-ghost'}" data-kf-action="${esc(a.id)}">${a.icon ?? ''}${esc(a.label)}</button>`)
        .join('');
    const searchHtml = opts.search
        ? `<div class="tbsearch">${KF_ICON.search}<input type="text" data-kf-search placeholder="${esc(opts.search.placeholder)}"></div>`
        : '';
    const segHtml = opts.segments?.length
        ? `<div class="segmented" data-kf-seg>${opts.segments
            .map((s, i) => `<button type="button" data-s="${esc(s.id)}" class="${i === 0 ? 'active' : ''}">${s.color ? `<span class="sd" style="background:${s.color}"></span>` : ''}${esc(s.label)} <span class="cnt" data-cnt="${esc(s.id)}">${s.count ?? 0}</span></button>`)
            .join('')}</div>`
        : '';
    const rightHtml = `<div class="tb-right"><span class="tb-meta" data-kf-meta></span>${opts.showColConfig
        ? `<button type="button" class="icon-btn" data-kf-action="cols" title="Spalten konfigurieren">${KF_ICON.columns}</button>`
        : ''}</div>`;
    const statusHtml = opts.status
        ? `<span class="hstatus"><span class="d"></span>${esc(opts.status)}</span>`
        : '';
    host.innerHTML = `
    <div class="kf-page">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="${esc(brandHref)}" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="${esc(opts.logoTitle ?? 'OTTO Market')}">${opts.logo ?? KF_LOGO_OTTO}</span>
          <span class="hdivider"></span>
          <div class="htitle">
            <div class="heyebrow">${esc(opts.eyebrow)}</div>
            <h1>${esc(opts.title)}</h1>
          </div>
          <div class="hactions">${statusHtml}${actionsHtml}</div>
        </header>
        <div class="card">
          <div class="toolbar">${searchHtml}${segHtml}${rightHtml}</div>
          <div class="grid-wrap"><div class="ag-theme-vario ag-theme-quartz" data-kf-grid></div></div>
        </div>
      </main>

      <div class="scrim" data-kf-scrim></div>
      <aside class="drawer" data-kf-drawer aria-hidden="true">
        <div class="dh">
          <div><div class="dt" data-kf-drawer-eyebrow>—</div><div class="dref" data-kf-drawer-ref>—</div></div>
          <button type="button" class="dclose" data-kf-drawer-close aria-label="Schließen">${KF_ICON.close}</button>
        </div>
        <div class="db" data-kf-drawer-body></div>
        <div class="dfoot" data-kf-drawer-foot></div>
      </aside>

      <div class="kf-toast" data-kf-toast>${KF_ICON.check}<span data-kf-toast-msg></span></div>
    </div>
  `;
    const root = host.querySelector('.kf-page');
    const q = (sel) => root.querySelector(sel);
    const gridEl = q('[data-kf-grid]');
    const metaEl = q('[data-kf-meta]');
    // ---- actions ----
    const actionHandlers = new Map();
    root.querySelectorAll('[data-kf-action]').forEach((btn) => {
        btn.addEventListener('click', () => actionHandlers.get(btn.dataset.kfAction)?.());
    });
    // ---- search ----
    let searchFn = null;
    const searchInput = root.querySelector('[data-kf-search]');
    searchInput?.addEventListener('input', () => searchFn?.(searchInput.value));
    // ---- segmented ----
    let segFn = null;
    const segWrap = root.querySelector('[data-kf-seg]');
    segWrap?.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-s]');
        if (!btn)
            return;
        segWrap.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        segFn?.(btn.dataset.s);
    });
    // ---- toast ----
    const toastEl = q('[data-kf-toast]');
    const toastMsg = q('[data-kf-toast-msg]');
    let toastTimer;
    const toast = (msg) => {
        toastMsg.textContent = msg;
        toastEl.classList.add('show');
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => toastEl.classList.remove('show'), 2200);
    };
    // ---- drawer ----
    const scrim = q('[data-kf-scrim]');
    const drawerEl = q('[data-kf-drawer]');
    const dEyebrow = q('[data-kf-drawer-eyebrow]');
    const dRef = q('[data-kf-drawer-ref]');
    const dBody = q('[data-kf-drawer-body]');
    const dFoot = q('[data-kf-drawer-foot]');
    let footHandler = null;
    const closeDrawer = () => {
        drawerEl.classList.remove('open');
        scrim.classList.remove('open');
        drawerEl.setAttribute('aria-hidden', 'true');
    };
    const openDrawer = (o) => {
        dEyebrow.textContent = o.eyebrow;
        dRef.textContent = o.ref;
        const fieldsHtml = o.fields
            .map((f) => `<div class="dfield${f.full ? ' full' : ''}"><div class="l">${esc(f.label)}</div><div class="v">${f.html}</div></div>`)
            .join('');
        const payloadHtml = o.payloadHtml
            ? `<div class="dsec-title">${esc(o.payloadTitle ?? 'Payload')}</div><div class="payload">${o.payloadHtml}</div>`
            : '';
        dBody.innerHTML = `<div class="dgrid">${fieldsHtml}</div>${payloadHtml}`;
        dFoot.innerHTML = o.footerHtml ?? '';
        footHandler = o.onFooter ?? null;
        drawerEl.classList.add('open');
        scrim.classList.add('open');
        drawerEl.setAttribute('aria-hidden', 'false');
    };
    q('[data-kf-drawer-close]').addEventListener('click', closeDrawer);
    scrim.addEventListener('click', closeDrawer);
    dFoot.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-kf-act]');
        if (btn)
            footHandler?.(btn.dataset.kfAct);
    });
    const onKey = (e) => {
        if (e.key === 'Escape')
            closeDrawer();
    };
    document.addEventListener('keydown', onKey);
    // ---- fit viewport height (iframe-Thematik) ----
    const fit = opts.fitViewport !== false;
    const fitViewport = () => {
        const h = window.innerHeight;
        if (h)
            root.style.height = h + 'px';
    };
    if (fit) {
        window.addEventListener('resize', fitViewport);
        window.addEventListener('orientationchange', fitViewport);
        fitViewport();
    }
    const destroy = () => {
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', fitViewport);
        window.removeEventListener('orientationchange', fitViewport);
        clearTimeout(toastTimer);
    };
    return {
        root,
        gridEl,
        metaEl,
        drawer: { open: openDrawer, close: closeDrawer },
        onAction: (id, fn) => actionHandlers.set(id, fn),
        onSearch: (fn) => {
            searchFn = fn;
        },
        onSegment: (fn) => {
            segFn = fn;
        },
        setSegmentCount: (id, n) => {
            const el = root.querySelector(`[data-cnt="${CSS.escape(id)}"]`);
            if (el)
                el.textContent = String(n);
        },
        setActiveSegment: (id) => {
            segWrap?.querySelectorAll('button[data-s]').forEach((b) => b.classList.toggle('active', b.dataset.s === id));
        },
        setMeta: (html) => {
            metaEl.innerHTML = html;
        },
        toast,
        destroy,
    };
}
// ---------------------------------------------------------------------------
// Eigenständige, wiederverwendbare Bausteine (ohne renderKfPage) — für Fenster,
// die ihr eigenes Layout bauen (z.B. der Bestellungen-Tab innerhalb der
// bestehenden Dashboard-Shell). `root` muss ein `.kf-page`-Element sein, damit
// die gescopten Styles greifen; Toast/Drawer sind `position:fixed` (Viewport).
// ---------------------------------------------------------------------------
/** Bottom-Center-Toast (Design). Hängt ein `.kf-toast` in `root` und liefert die show-Funktion. */
export function createKfToast(root) {
    const el = document.createElement('div');
    el.className = 'kf-toast';
    el.innerHTML = `${KF_ICON.check}<span></span>`;
    root.appendChild(el);
    const msgEl = el.querySelector('span');
    let timer;
    return (msg) => {
        msgEl.textContent = msg;
        el.classList.add('show');
        clearTimeout(timer);
        timer = setTimeout(() => el.classList.remove('show'), 2200);
    };
}
/** Generischer Slide-in-Drawer (Scrim + Aside) für beliebigen HTML-Inhalt.
 *  Schließt via Scrim-Klick, ESC und jedes `[data-kf-drawer-close]`-Element. */
export function createKfDrawer(root) {
    const scrim = document.createElement('div');
    scrim.className = 'scrim';
    const aside = document.createElement('aside');
    aside.className = 'drawer';
    aside.setAttribute('aria-hidden', 'true');
    const content = document.createElement('div');
    aside.appendChild(content);
    root.appendChild(scrim);
    root.appendChild(aside);
    let onCloseCb = null;
    const close = () => {
        if (aside.getAttribute('aria-hidden') === 'true')
            return;
        aside.classList.remove('open');
        scrim.classList.remove('open');
        aside.setAttribute('aria-hidden', 'true');
        const cb = onCloseCb;
        onCloseCb = null;
        cb?.();
    };
    const open = (o) => {
        content.innerHTML = o.html;
        aside.classList.toggle('wide', !!o.wide);
        onCloseCb = o.onClose ?? null;
        content.querySelector('[data-kf-drawer-close]')?.addEventListener('click', close);
        o.onMount?.(content);
        aside.classList.add('open');
        scrim.classList.add('open');
        aside.setAttribute('aria-hidden', 'false');
    };
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape')
            close();
    });
    return { open, close };
}
//# sourceMappingURL=kf-page.js.map